/**
 * AxLabs Physics Experiments - Electromagnetism
 *
 * Collection of electromagnetism experiments covering electrostatics,
 * circuits, magnetic fields, induction, and oscillations.
 */
import { createExperimentFromSchema, STANDARD_GRAVITY } from '../../framework/index.js';
import { getParam, getState, getStateValues } from './helpers.js';
// Physical constants
const EPSILON_0 = 8.854187817e-12; // Vacuum permittivity (F/m)
// ============================================================================
// COULOMB'S LAW (Point Charges)
// ============================================================================
export const CoulombsLaw = createExperimentFromSchema({
    id: 'coulombs-law',
    name: "Coulomb's Law (Point Charges)",
    domain: 'physics',
    description: 'Electrostatic force between two point charges',
    conceptTags: ['Electrostatics', 'Coulomb\'s Law', 'Electric Force', 'Point Charges'],
    difficulty: 3,
    stateVariables: [
        { id: 'x1', name: 'Charge 1 X Position', unit: 'm', initial: -0.1, symbol: 'x_1' },
        { id: 'x2', name: 'Charge 2 X Position', unit: 'm', initial: 0.1, symbol: 'x_2' },
        { id: 'v1', name: 'Charge 1 Velocity', unit: 'm/s', initial: 0, symbol: 'v_1' },
        { id: 'v2', name: 'Charge 2 Velocity', unit: 'm/s', initial: 0, symbol: 'v_2' },
    ],
    parameters: [
        { id: 'q1', name: 'Charge 1', unit: 'C', min: -1e-6, max: 1e-6, default: 1e-9, symbol: 'q_1' },
        { id: 'q2', name: 'Charge 2', unit: 'C', min: -1e-6, max: 1e-6, default: -1e-9, symbol: 'q_2' },
        { id: 'm1', name: 'Mass 1', unit: 'kg', min: 1e-15, max: 1e-6, default: 1e-12, symbol: 'm_1' },
        { id: 'm2', name: 'Mass 2', unit: 'kg', min: 1e-15, max: 1e-6, default: 1e-12, symbol: 'm_2' },
    ],
    equations: [
        {
            id: 'coulomb-force',
            name: "Coulomb's Law",
            latex: 'F = k_e\\frac{|q_1q_2|}{r^2}',
            description: 'Magnitude of electrostatic force between charges',
        },
        {
            id: 'coulomb-constant',
            name: 'Coulomb Constant',
            latex: 'k_e = \\frac{1}{4\\pi\\epsilon_0} \\approx 8.99 \\times 10^9 \\text{ N·m}^2/\\text{C}^2',
            description: 'Electrostatic constant',
        },
        {
            id: 'vector-force',
            name: 'Vector Force',
            latex: '\\vec{F}_{12} = k_e\\frac{q_1q_2}{r^2}\\hat{r}_{21}',
            description: 'Force on q1 due to q2 (attractive if opposite signs)',
        },
    ],
    derivatives: (state, params) => {
        const x1 = getState(state, 'x1');
        const x2 = getState(state, 'x2');
        const q1 = getParam(params, 'q1');
        const q2 = getParam(params, 'q2');
        const m1 = getParam(params, 'm1');
        const m2 = getParam(params, 'm2');
        const r = x2 - x1;
        const r_mag = Math.abs(r);
        const r_safe = Math.max(r_mag, 0.001); // Prevent singularity
        const ke = 1 / (4 * Math.PI * EPSILON_0);
        const F_mag = ke * Math.abs(q1 * q2) / (r_safe * r_safe);
        const F_on_1 = (q1 * q2 > 0 ? -1 : 1) * F_mag; // Repel/attract
        const F_on_2 = -F_on_1;
        const a1 = F_on_1 / m1;
        const a2 = F_on_2 / m2;
        return {
            x1: getState(state, 'v1'),
            x2: getState(state, 'v2'),
            v1: a1,
            v2: a2,
        };
    },
    derivedState: (state, params) => {
        const x1 = getState(state, 'x1');
        const x2 = getState(state, 'x2');
        const q1 = getParam(params, 'q1');
        const q2 = getParam(params, 'q2');
        const r = Math.abs(x2 - x1);
        const ke = 1 / (4 * Math.PI * EPSILON_0);
        const F = ke * Math.abs(q1 * q2) / (r * r);
        return {
            separation: r,
            force_magnitude: F,
            is_repulsive: q1 * q2 > 0 ? 1 : 0,
            potential_energy: ke * q1 * q2 / r,
        };
    },
    energyComputation: (state, params) => {
        const x1 = getState(state, 'x1');
        const x2 = getState(state, 'x2');
        const q1 = getParam(params, 'q1');
        const q2 = getParam(params, 'q2');
        const m1 = getParam(params, 'm1');
        const m2 = getParam(params, 'm2');
        const v1 = getState(state, 'v1');
        const v2 = getState(state, 'v2');
        const r = Math.abs(x2 - x1);
        const ke = 1 / (4 * Math.PI * EPSILON_0);
        const ke_num = 0.5 * m1 * v1 * v1 + 0.5 * m2 * v2 * v2;
        const pe = ke * q1 * q2 / r;
        return { kinetic: ke_num, potential: pe, thermal: 0, total: ke_num + pe };
    },
    failureModes: [
        {
            id: 'charges-too-close',
            name: 'Charges Too Close',
            check: (state, _params) => {
                const stateVals = getStateValues(state);
                const x1 = getState(stateVals, 'x1');
                const x2 = getState(stateVals, 'x2');
                return Math.abs(x2 - x1) < 0.001;
            },
            severity: 'warning',
            educationalMessage: 'The charges are very close together. In reality, quantum effects would dominate and the point charge approximation breaks down.',
        },
        {
            id: 'negative-mass',
            name: 'Negative Mass',
            check: (_state, params) => {
                const m1 = getParam(params, 'm1');
                const m2 = getParam(params, 'm2');
                return m1 <= 0 || m2 <= 0;
            },
            severity: 'error',
            educationalMessage: 'Mass must be positive for physical validity.',
        },
    ],
    bounds: {
        x1: { min: -1, max: 1 },
        x2: { min: -1, max: 1 },
        v1: { min: -100, max: 100 },
        v2: { min: -100, max: 100 },
    },
});
// ============================================================================
// ELECTRIC FIELD FROM POINT CHARGE
// ============================================================================
export const ElectricFieldPointCharge = createExperimentFromSchema({
    id: 'electric-field-point-charge',
    name: 'Electric Field from Point Charge',
    domain: 'physics',
    description: 'Electric field calculation at various points from a point charge',
    conceptTags: ['Electric Field', 'Point Charge', 'Field Lines', 'Superposition'],
    difficulty: 3,
    stateVariables: [
        { id: 'x', name: 'Test Point X', unit: 'm', initial: 0.1, symbol: 'x' },
        { id: 'y', name: 'Test Point Y', unit: 'm', initial: 0, symbol: 'y' },
    ],
    parameters: [
        { id: 'Q', name: 'Source Charge', unit: 'C', min: -1e-6, max: 1e-6, default: 1e-9, symbol: 'Q' },
        { id: 'q_test', name: 'Test Charge', unit: 'C', min: -1e-9, max: 1e-9, default: 1e-12, symbol: 'q_{test}' },
    ],
    equations: [
        {
            id: 'e-field',
            name: 'Electric Field',
            latex: '\\vec{E} = k_e\\frac{Q}{r^2}\\hat{r}',
            description: 'Electric field at distance r from point charge Q',
        },
        {
            id: 'field-force',
            name: 'Force on Test Charge',
            latex: '\\vec{F} = q_{test}\\vec{E}',
            description: 'Force experienced by a test charge in the field',
        },
        {
            id: 'field-magnitude',
            name: 'Field Magnitude',
            latex: '|\\vec{E}| = \\frac{|Q|}{4\\pi\\epsilon_0 r^2}',
            description: 'Magnitude of electric field',
        },
    ],
    derivatives: () => ({
        x: 0,
        y: 0,
    }),
    derivedState: (state, params) => {
        const x = getState(state, 'x');
        const y = getState(state, 'y');
        const Q = getParam(params, 'Q');
        const q_test = getParam(params, 'q_test');
        const r = Math.sqrt(x * x + y * y);
        const r_safe = Math.max(r, 0.001);
        const ke = 1 / (4 * Math.PI * EPSILON_0);
        const E_mag = ke * Math.abs(Q) / (r_safe * r_safe);
        const Ex = E_mag * (Q > 0 ? 1 : -1) * (x / r_safe);
        const Ey = E_mag * (Q > 0 ? 1 : -1) * (y / r_safe);
        const Fx = q_test * Ex;
        const Fy = q_test * Ey;
        return {
            electric_field_magnitude: E_mag,
            electric_field_x: Ex,
            electric_field_y: Ey,
            force_x: Fx,
            force_y: Fy,
            force_magnitude: Math.abs(q_test) * E_mag,
            potential: ke * Q / r_safe,
        };
    },
    energyComputation: (state, params) => {
        const x = getState(state, 'x');
        const y = getState(state, 'y');
        const Q = getParam(params, 'Q');
        const q_test = getParam(params, 'q_test');
        const r = Math.sqrt(x * x + y * y);
        const ke = 1 / (4 * Math.PI * EPSILON_0);
        const pe = ke * Q * q_test / r;
        return { kinetic: 0, potential: pe, thermal: 0, total: pe };
    },
    bounds: {
        x: { min: -1, max: 1 },
        y: { min: -1, max: 1 },
    },
});
// ============================================================================
// PARALLEL PLATE CAPACITOR
// ============================================================================
export const ParallelPlateCapacitor = createExperimentFromSchema({
    id: 'parallel-plate-capacitor',
    name: 'Parallel Plate Capacitor',
    domain: 'physics',
    description: 'Capacitance, electric field, and stored energy in a parallel plate capacitor',
    conceptTags: ['Capacitance', 'Electric Field', 'Energy Storage', 'Dielectrics'],
    difficulty: 4,
    stateVariables: [
        { id: 'Q', name: 'Stored Charge', unit: 'C', initial: 0, symbol: 'Q' },
    ],
    parameters: [
        { id: 'A', name: 'Plate Area', unit: 'm²', min: 1e-4, max: 1, default: 0.01, symbol: 'A' },
        { id: 'd', name: 'Plate Separation', unit: 'm', min: 1e-4, max: 0.1, default: 0.001, symbol: 'd' },
        { id: 'epsilon_r', name: 'Dielectric Constant', unit: '', min: 1, max: 10, default: 1, symbol: '\\epsilon_r', description: 'Relative permittivity (1 = vacuum/air)' },
        { id: 'V_max', name: 'Breakdown Voltage', unit: 'V', min: 100, max: 10000, default: 1000, symbol: 'V_{bd}' },
    ],
    equations: [
        {
            id: 'capacitance',
            name: 'Capacitance',
            latex: 'C = \\frac{\\epsilon_r\\epsilon_0 A}{d}',
            description: 'Capacitance of parallel plate capacitor',
        },
        {
            id: 'electric-field-cap',
            name: 'Electric Field Between Plates',
            latex: 'E = \\frac{V}{d} = \\frac{Q}{\\epsilon_r\\epsilon_0 A}',
            description: 'Uniform electric field between plates',
        },
        {
            id: 'stored-energy',
            name: 'Stored Energy',
            latex: 'U = \\frac{Q^2}{2C} = \\frac{1}{2}CV^2 = \\frac{1}{2}QV',
            description: 'Energy stored in capacitor',
        },
    ],
    derivatives: () => ({
        Q: 0,
    }),
    derivedState: (state, params) => {
        const Q = getState(state, 'Q');
        const A = getParam(params, 'A');
        const d = getParam(params, 'd');
        const epsilon_r = getParam(params, 'epsilon_r');
        const V_max = getParam(params, 'V_max');
        const C = (epsilon_r * EPSILON_0 * A) / d;
        const V = Q / C;
        const E = Q / (epsilon_r * EPSILON_0 * A);
        const U = (Q * Q) / (2 * C);
        return {
            capacitance: C,
            voltage: V,
            electric_field: E,
            stored_energy: U,
            energy_density: U / (A * d),
            charge_density: Q / A,
            voltage_stress: V / V_max,
            near_breakdown: V > V_max * 0.9 ? 1 : 0,
        };
    },
    energyComputation: (state, params) => {
        const Q = getState(state, 'Q');
        const A = getParam(params, 'A');
        const d = getParam(params, 'd');
        const epsilon_r = getParam(params, 'epsilon_r');
        const C = (epsilon_r * EPSILON_0 * A) / d;
        const U = (Q * Q) / (2 * C);
        return { kinetic: 0, potential: U, thermal: 0, total: U };
    },
    failureModes: [
        {
            id: 'breakdown',
            name: 'Dielectric Breakdown',
            check: (state, params) => {
                const Q = getState(getStateValues(state), 'Q');
                const A = getParam(params, 'A');
                const d = getParam(params, 'd');
                const epsilon_r = getParam(params, 'epsilon_r');
                const V_max = getParam(params, 'V_max');
                const C = (epsilon_r * EPSILON_0 * A) / d;
                const V = Q / C;
                return V >= V_max;
            },
            severity: 'critical',
            educationalMessage: 'Dielectric breakdown! The voltage exceeds the breakdown voltage of the capacitor. In reality, sparking would occur and the capacitor would discharge.',
        },
    ],
    bounds: {
        Q: { min: -1e-6, max: 1e-6 },
    },
});
// ============================================================================
// RL CIRCUIT TRANSIENT
// ============================================================================
export const RLCircuitTransient = createExperimentFromSchema({
    id: 'rl-circuit-transient',
    name: 'RL Circuit Transient',
    domain: 'physics',
    description: 'Current growth and decay in an RL circuit',
    conceptTags: ['Inductance', 'Transient Response', 'Time Constant', 'Self-Inductance'],
    difficulty: 4,
    stateVariables: [
        { id: 'I', name: 'Current', unit: 'A', initial: 0, symbol: 'I' },
        { id: 't', name: 'Time', unit: 's', initial: 0, symbol: 't' },
    ],
    parameters: [
        { id: 'V0', name: 'Source Voltage', unit: 'V', min: 1, max: 100, default: 12, symbol: 'V_0' },
        { id: 'R', name: 'Resistance', unit: 'Ω', min: 1, max: 1000, default: 10, symbol: 'R' },
        { id: 'L', name: 'Inductance', unit: 'H', min: 0.001, max: 10, default: 0.1, symbol: 'L' },
        { id: 'switch_closed', name: 'Switch Closed', unit: '', min: 0, max: 1, default: 1, symbol: 'S' },
    ],
    equations: [
        {
            id: 'rl-equation',
            name: 'RL Circuit Equation',
            latex: 'V_0 = IR + L\\frac{dI}{dt}',
            description: 'Kirchhoff\'s voltage law for RL circuit',
        },
        {
            id: 'time-constant-rl',
            name: 'Time Constant',
            latex: '\\tau = \\frac{L}{R}',
            description: 'Characteristic time for current changes',
        },
        {
            id: 'current-growth',
            name: 'Current Growth',
            latex: 'I(t) = \\frac{V_0}{R}\\left(1 - e^{-t/\\tau}\\right)',
            description: 'Current when switch closes at t=0',
        },
    ],
    derivatives: (state, params) => {
        const I = getState(state, 'I');
        const V0 = getParam(params, 'V0');
        const R = getParam(params, 'R');
        const L = getParam(params, 'L');
        const switch_closed = getParam(params, 'switch_closed');
        let dI_dt = 0;
        if (switch_closed > 0.5) {
            dI_dt = (V0 - I * R) / L;
        }
        else {
            dI_dt = -(I * R) / L; // Decay when open
        }
        return {
            I: dI_dt,
            t: 1,
        };
    },
    derivedState: (state, params) => {
        const I = getState(state, 'I');
        const V0 = getParam(params, 'V0');
        const R = getParam(params, 'R');
        const L = getParam(params, 'L');
        const tau = L / R;
        const I_max = V0 / R;
        const VL = V0 - I * R;
        return {
            time_constant: tau,
            max_current: I_max,
            current_ratio: I / I_max,
            inductor_voltage: VL,
            power_resistor: I * I * R,
            power_inductor: I * VL,
            energy_stored: 0.5 * L * I * I,
        };
    },
    energyComputation: (state, params) => {
        const I = getState(state, 'I');
        const L = getParam(params, 'L');
        const energy_inductor = 0.5 * L * I * I;
        return { kinetic: 0, potential: energy_inductor, thermal: 0, total: energy_inductor };
    },
    bounds: {
        I: { min: -10, max: 20 },
        t: { min: 0, max: 10 },
    },
});
// ============================================================================
// RLC SERIES RESONANCE
// ============================================================================
export const RLCSeriesResonance = createExperimentFromSchema({
    id: 'rlc-series-resonance',
    name: 'RLC Series Resonance',
    domain: 'physics',
    description: 'Series RLC circuit with AC driving frequency',
    conceptTags: ['AC Circuits', 'Resonance', 'Impedance', 'Quality Factor'],
    difficulty: 6,
    stateVariables: [
        { id: 'Q', name: 'Charge on Capacitor', unit: 'C', initial: 0, symbol: 'Q' },
        { id: 'I', name: 'Current', unit: 'A', initial: 0, symbol: 'I' },
    ],
    parameters: [
        { id: 'V0', name: 'Peak Voltage', unit: 'V', min: 1, max: 100, default: 10, symbol: 'V_0' },
        { id: 'omega', name: 'Driving Frequency', unit: 'rad/s', min: 1, max: 10000, default: 100, symbol: '\\omega' },
        { id: 'R', name: 'Resistance', unit: 'Ω', min: 1, max: 1000, default: 10, symbol: 'R' },
        { id: 'L', name: 'Inductance', unit: 'H', min: 0.001, max: 1, default: 0.1, symbol: 'L' },
        { id: 'C', name: 'Capacitance', unit: 'F', min: 1e-6, max: 0.01, default: 1e-4, symbol: 'C' },
    ],
    equations: [
        {
            id: 'rlc-series-eq',
            name: 'Series RLC Equation',
            latex: 'V_0\\cos(\\omega t) = IR + L\\frac{dI}{dt} + \\frac{Q}{C}',
            description: 'Kirchhoff\'s voltage law for series RLC',
        },
        {
            id: 'impedance-series',
            name: 'Series Impedance',
            latex: 'Z = \\sqrt{R^2 + (\\omega L - \\frac{1}{\\omega C})^2}',
            description: 'Total impedance of series RLC circuit',
        },
        {
            id: 'resonant-freq',
            name: 'Resonant Frequency',
            latex: '\\omega_0 = \\frac{1}{\\sqrt{LC}}',
            description: 'Frequency at which reactances cancel',
        },
        {
            id: 'quality-factor',
            name: 'Quality Factor',
            latex: 'Q = \\frac{\\omega_0 L}{R}',
            description: 'Measure of resonance sharpness',
        },
    ],
    derivatives: (state, params) => {
        const Q = getState(state, 'Q');
        const I = getState(state, 'I');
        const V0 = getParam(params, 'V0');
        const R = getParam(params, 'R');
        const L = getParam(params, 'L');
        const C = getParam(params, 'C');
        // Use time 0 for derivative - the simulation framework tracks actual time
        const V_drive = V0; // At t=0, cos(0) = 1
        const dI_dt = (V_drive - I * R - Q / C) / L;
        return {
            Q: I,
            I: dI_dt,
        };
    },
    derivedState: (_state, params) => {
        const V0 = getParam(params, 'V0');
        const omega = getParam(params, 'omega');
        const R = getParam(params, 'R');
        const L = getParam(params, 'L');
        const C = getParam(params, 'C');
        const omega_0 = 1 / Math.sqrt(L * C);
        const X_L = omega * L;
        const X_C = 1 / (omega * C);
        const X_net = X_L - X_C;
        const Z = Math.sqrt(R * R + X_net * X_net);
        const I_peak = V0 / Z;
        const Q_factor = omega_0 * L / R;
        const bandwidth = omega_0 / Q_factor;
        // Phase angle
        const phi = Math.atan2(X_net, R);
        return {
            resonant_frequency: omega_0,
            inductive_reactance: X_L,
            capacitive_reactance: X_C,
            net_reactance: X_net,
            impedance: Z,
            peak_current: I_peak,
            quality_factor: Q_factor,
            bandwidth: bandwidth,
            phase_angle: phi,
            is_resonant: Math.abs(omega - omega_0) < bandwidth / 2 ? 1 : 0,
            power_factor: Math.cos(phi),
        };
    },
    energyComputation: (state, params) => {
        const Q = getState(state, 'Q');
        const I = getState(state, 'I');
        const L = getParam(params, 'L');
        const C = getParam(params, 'C');
        const E_L = 0.5 * L * I * I;
        const E_C = 0.5 * Q * Q / C;
        return { kinetic: E_L, potential: E_C, thermal: 0, total: E_L + E_C };
    },
    failureModes: [
        {
            id: 'negative-c',
            name: 'Invalid Capacitance',
            check: (_state, params) => {
                const C = getParam(params, 'C');
                return C <= 0;
            },
            severity: 'error',
            educationalMessage: 'Capacitance must be positive.',
        },
        {
            id: 'negative-l',
            name: 'Invalid Inductance',
            check: (_state, params) => {
                const L = getParam(params, 'L');
                return L <= 0;
            },
            severity: 'error',
            educationalMessage: 'Inductance must be positive.',
        },
    ],
});
// ============================================================================
// RLC PARALLEL RESONANCE
// ============================================================================
export const RLCParallelResonance = createExperimentFromSchema({
    id: 'rlc-parallel-resonance',
    name: 'RLC Parallel Resonance',
    domain: 'physics',
    description: 'Parallel RLC circuit (tank circuit) with AC current source',
    conceptTags: ['Parallel Resonance', 'Tank Circuit', 'Admittance', 'Antiresonance'],
    difficulty: 6,
    stateVariables: [
        { id: 'V', name: 'Voltage', unit: 'V', initial: 0, symbol: 'V' },
    ],
    parameters: [
        { id: 'I0', name: 'Peak Current', unit: 'A', min: 0.001, max: 10, default: 0.1, symbol: 'I_0' },
        { id: 'omega', name: 'Driving Frequency', unit: 'rad/s', min: 1, max: 10000, default: 100, symbol: '\\omega' },
        { id: 'R', name: 'Resistance', unit: 'Ω', min: 1, max: 10000, default: 100, symbol: 'R' },
        { id: 'L', name: 'Inductance', unit: 'H', min: 0.001, max: 1, default: 0.1, symbol: 'L' },
        { id: 'C', name: 'Capacitance', unit: 'F', min: 1e-9, max: 0.01, default: 1e-5, symbol: 'C' },
    ],
    equations: [
        {
            id: 'admittance',
            name: 'Parallel Admittance',
            latex: 'Y = \\sqrt{\\frac{1}{R^2} + (\\omega C - \\frac{1}{\\omega L})^2}',
            description: 'Total admittance of parallel RLC',
        },
        {
            id: 'impedance-parallel',
            name: 'Parallel Impedance',
            latex: 'Z = \\frac{1}{Y} = \\frac{1}{\\sqrt{\\frac{1}{R^2} + (\\omega C - \\frac{1}{\\omega L})^2}}',
            description: 'Impedance is maximum at resonance',
        },
        {
            id: 'resonant-freq-parallel',
            name: 'Parallel Resonant Frequency',
            latex: '\\omega_0 = \\frac{1}{\\sqrt{LC}}',
            description: 'Same as series for ideal components',
        },
        {
            id: 'dynamic-impedance',
            name: 'Dynamic Impedance at Resonance',
            latex: 'Z_{dynamic} = \\frac{L}{RC} = Q^2 R',
            description: 'Maximum impedance at resonance',
        },
    ],
    derivatives: () => ({
        V: 0,
    }),
    derivedState: (_state, params) => {
        const I0 = getParam(params, 'I0');
        const omega = getParam(params, 'omega');
        const R = getParam(params, 'R');
        const L = getParam(params, 'L');
        const C = getParam(params, 'C');
        const omega_0 = 1 / Math.sqrt(L * C);
        const X_L = omega * L;
        // Parallel admittance
        const G = 1 / R; // Conductance
        const B_L = -1 / X_L; // Inductive susceptance
        const B_C = omega * C; // Capacitive susceptance
        const B_net = B_C + B_L; // Net susceptance
        const Y = Math.sqrt(G * G + B_net * B_net);
        const Z = 1 / Y;
        const V_peak = I0 * Z;
        const Q_factor = R * Math.sqrt(C / L);
        return {
            resonant_frequency: omega_0,
            admittance: Y,
            impedance: Z,
            peak_voltage: V_peak,
            quality_factor: Q_factor,
            conductance: G,
            susceptance: B_net,
            impedance_max: R * Q_factor * Q_factor,
            is_resonant: Math.abs(omega - omega_0) < omega_0 / Q_factor / 2 ? 1 : 0,
            current_in_phase: I0 * G / Y,
            current_out_of_phase: I0 * Math.abs(B_net) / Y,
        };
    },
    energyComputation: (_state, params) => {
        const L = getParam(params, 'L');
        const C = getParam(params, 'C');
        // At resonance with peak voltage V, energy oscillates between L and C
        // Total energy stored in the tank circuit
        return {
            kinetic: 0, // Energy in inductor (magnetic)
            potential: 0, // Energy in capacitor (electric)
            thermal: 0,
            total: 2 * Math.sqrt(L * C), // Proportional to LC time constant
        };
    },
    bounds: {
        V: { min: -200, max: 200 },
    },
});
// ============================================================================
// MAGNETIC FORCE ON WIRE
// ============================================================================
export const MagneticForceWire = createExperimentFromSchema({
    id: 'magnetic-force-wire',
    name: 'Magnetic Force on Wire',
    domain: 'physics',
    description: 'Force on a current-carrying wire in a magnetic field',
    conceptTags: ['Lorentz Force', 'Magnetic Fields', 'Motor Effect', 'Fleming\'s Rule'],
    difficulty: 4,
    stateVariables: [
        { id: 'v', name: 'Wire Velocity', unit: 'm/s', initial: 0, symbol: 'v' },
        { id: 'y', name: 'Wire Position', unit: 'm', initial: 0, symbol: 'y' },
    ],
    parameters: [
        { id: 'I', name: 'Current', unit: 'A', min: -10, max: 10, default: 5, symbol: 'I' },
        { id: 'L', name: 'Wire Length', unit: 'm', min: 0.01, max: 1, default: 0.2, symbol: 'L' },
        { id: 'B', name: 'Magnetic Field', unit: 'T', min: -2, max: 2, default: 0.5, symbol: 'B' },
        { id: 'm', name: 'Wire Mass', unit: 'kg', min: 0.001, max: 1, default: 0.01, symbol: 'm' },
    ],
    equations: [
        {
            id: 'force-on-wire',
            name: 'Force on Current-Carrying Wire',
            latex: '\\vec{F} = I\\vec{L} \\times \\vec{B}',
            description: 'Force is perpendicular to both current and field',
        },
        {
            id: 'force-magnitude',
            name: 'Force Magnitude',
            latex: 'F = ILB\\sin\\theta',
            description: 'For perpendicular wire and field: F = ILB',
        },
        {
            id: 'direction',
            name: 'Fleming\'s Left-Hand Rule',
            latex: '\\text{Thumb: Force, First finger: Field, Second finger: Current}',
            description: 'Mnemonic for force direction on current-carrying conductor',
        },
    ],
    derivatives: (state, params) => {
        const I = getParam(params, 'I');
        const L = getParam(params, 'L');
        const B = getParam(params, 'B');
        const m = getParam(params, 'm');
        const g = STANDARD_GRAVITY;
        const F_magnetic = I * L * B;
        const F_gravity = -m * g;
        const a = (F_magnetic + F_gravity) / m;
        return {
            v: a,
            y: getState(state, 'v'),
        };
    },
    derivedState: (_state, params) => {
        const I = getParam(params, 'I');
        const L = getParam(params, 'L');
        const B = getParam(params, 'B');
        const m = getParam(params, 'm');
        const g = STANDARD_GRAVITY;
        const F_magnetic = I * L * B;
        const F_gravity = -m * g;
        const F_net = F_magnetic + F_gravity;
        const a = F_net / m;
        const I_balance = (m * g) / (L * B);
        return {
            magnetic_force: F_magnetic,
            gravitational_force: F_gravity,
            net_force: F_net,
            acceleration: a,
            current_to_balance: I_balance,
            is_balanced: Math.abs(I - I_balance) < 0.01 ? 1 : 0,
            power_dissipated: I * I * 0.1, // Assuming some resistance
        };
    },
    failureModes: [
        {
            id: 'no-field',
            name: 'No Magnetic Field',
            check: (_state, params) => {
                const B = getParam(params, 'B');
                return Math.abs(B) < 0.01;
            },
            severity: 'warning',
            educationalMessage: 'Magnetic field is too weak. No significant magnetic force will act on the wire.',
        },
        {
            id: 'negative-mass-wire',
            name: 'Negative Mass',
            check: (_state, params) => {
                const m = getParam(params, 'm');
                return m <= 0;
            },
            severity: 'error',
            educationalMessage: 'Wire mass must be positive for physical validity.',
        },
    ],
    bounds: {
        v: { min: -10, max: 10 },
        y: { min: -1, max: 1 },
    },
});
// ============================================================================
// FARADAY'S LAW (EMF INDUCTION)
// ============================================================================
export const FaradayLaw = createExperimentFromSchema({
    id: 'faraday-law',
    name: "Faraday's Law (EMF Induction)",
    domain: 'physics',
    description: 'Electromotive force induced by changing magnetic flux',
    conceptTags: ['Faraday\'s Law', 'Magnetic Flux', 'EMF', 'Lenz\'s Law'],
    difficulty: 5,
    stateVariables: [
        { id: 'theta', name: 'Loop Angle', unit: 'rad', initial: 0, symbol: '\\theta' },
        { id: 'omega', name: 'Angular Velocity', unit: 'rad/s', initial: 2, symbol: '\\omega' },
    ],
    parameters: [
        { id: 'B', name: 'Magnetic Field', unit: 'T', min: 0.1, max: 2, default: 0.5, symbol: 'B' },
        { id: 'A', name: 'Loop Area', unit: 'm²', min: 0.001, max: 1, default: 0.01, symbol: 'A' },
        { id: 'N', name: 'Number of Turns', unit: '', min: 1, max: 1000, default: 100, symbol: 'N' },
        { id: 'R_load', name: 'Load Resistance', unit: 'Ω', min: 1, max: 1000, default: 10, symbol: 'R_{load}' },
    ],
    equations: [
        {
            id: 'faraday-law',
            name: "Faraday's Law",
            latex: '\\mathcal{E} = -N\\frac{d\\Phi_B}{dt}',
            description: 'Induced EMF equals negative rate of change of flux',
        },
        {
            id: 'magnetic-flux',
            name: 'Magnetic Flux',
            latex: '\\Phi_B = BA\\cos(\\theta)',
            description: 'Magnetic flux through the loop',
        },
        {
            id: 'induced-emf',
            name: 'Induced EMF (Rotating Loop)',
            latex: '\\mathcal{E} = NBA\\omega\\sin(\\omega t)',
            description: 'EMF for loop rotating at constant angular velocity',
        },
        {
            id: 'lenz-law',
            name: "Lenz's Law",
            latex: '\\mathcal{E} = -\\frac{d\\Phi_B}{dt}',
            description: 'Induced effects oppose the change that created them',
        },
    ],
    derivatives: (state, _params) => {
        const omega = getState(state, 'omega');
        return {
            theta: omega,
            omega: 0,
        };
    },
    derivedState: (state, params) => {
        const theta = getState(state, 'theta');
        const omega = getState(state, 'omega');
        const B = getParam(params, 'B');
        const A = getParam(params, 'A');
        const N = getParam(params, 'N');
        const R_load = getParam(params, 'R_load');
        const flux = B * A * Math.cos(theta);
        const flux_rate = -B * A * omega * Math.sin(theta);
        const emf = -N * flux_rate; // Faraday's law
        const induced_current = emf / R_load;
        const power = emf * emf / R_load;
        return {
            magnetic_flux: flux,
            flux_rate_of_change: flux_rate,
            induced_emf: emf,
            induced_current: induced_current,
            power_output: power,
            peak_emf: N * B * A * omega,
            frequency: omega / (2 * Math.PI),
        };
    },
    energyComputation: (state, params) => {
        const theta = getState(state, 'theta');
        const omega = getState(state, 'omega');
        const B = getParam(params, 'B');
        const A = getParam(params, 'A');
        const N = getParam(params, 'N');
        const R_load = getParam(params, 'R_load');
        const emf = N * B * A * omega * Math.sin(theta);
        const instantaneous_power = (emf * emf) / R_load;
        return {
            kinetic: 0,
            potential: 0,
            thermal: instantaneous_power,
            total: instantaneous_power,
        };
    },
    failureModes: [
        {
            id: 'no-flux-change',
            name: 'No Flux Change',
            check: (state, _params) => {
                const omega = getState(getStateValues(state), 'omega');
                return Math.abs(omega) < 0.01;
            },
            severity: 'warning',
            educationalMessage: 'The loop is not rotating, so there is no change in magnetic flux. According to Faraday\'s law, no EMF is induced.',
        },
    ],
    bounds: {
        theta: { min: -Infinity, max: Infinity },
        omega: { min: -20, max: 20 },
    },
});
// ============================================================================
// TRANSFORMER (IDEAL)
// ============================================================================
export const IdealTransformer = createExperimentFromSchema({
    id: 'ideal-transformer',
    name: 'Transformer (Ideal)',
    domain: 'physics',
    description: 'Ideal transformer with voltage transformation and power transfer',
    conceptTags: ['Transformers', 'Mutual Inductance', 'Voltage Ratio', 'Power Transfer'],
    difficulty: 5,
    stateVariables: [
        { id: 'V_in', name: 'Input Voltage', unit: 'V', initial: 120, symbol: 'V_{in}' },
    ],
    parameters: [
        { id: 'N_primary', name: 'Primary Turns', unit: '', min: 10, max: 10000, default: 1000, symbol: 'N_p' },
        { id: 'N_secondary', name: 'Secondary Turns', unit: '', min: 10, max: 10000, default: 100, symbol: 'N_s' },
        { id: 'R_load', name: 'Load Resistance', unit: 'Ω', min: 1, max: 10000, default: 10, symbol: 'R_{load}' },
        { id: 'f', name: 'Frequency', unit: 'Hz', min: 1, max: 1000, default: 60, symbol: 'f' },
    ],
    equations: [
        {
            id: 'voltage-ratio',
            name: 'Voltage Transformation',
            latex: '\\frac{V_s}{V_p} = \\frac{N_s}{N_p}',
            description: 'Voltage ratio equals turns ratio',
        },
        {
            id: 'current-ratio',
            name: 'Current Transformation',
            latex: '\\frac{I_s}{I_p} = \\frac{N_p}{N_s}',
            description: 'Current ratio is inverse of turns ratio',
        },
        {
            id: 'power-conservation',
            name: 'Power Conservation (Ideal)',
            latex: 'V_p I_p = V_s I_s',
            description: 'Input power equals output power in ideal transformer',
        },
        {
            id: 'impedance-reflection',
            name: 'Impedance Reflection',
            latex: 'Z_{in} = \\left(\\frac{N_p}{N_s}\\right)^2 Z_{load}',
            description: 'Load impedance reflected to primary',
        },
    ],
    derivatives: () => ({
        V_in: 0,
    }),
    derivedState: (state, params) => {
        const V_in = getState(state, 'V_in');
        const N_p = getParam(params, 'N_primary');
        const N_s = getParam(params, 'N_secondary');
        const R_load = getParam(params, 'R_load');
        const turns_ratio = N_s / N_p;
        const V_out = V_in * turns_ratio;
        const I_out = V_out / R_load;
        const I_in = I_out * turns_ratio;
        const P_in = V_in * I_in;
        const P_out = V_out * I_out;
        const Z_reflected = (N_p / N_s) ** 2 * R_load;
        return {
            turns_ratio,
            output_voltage: V_out,
            input_current: I_in,
            output_current: I_out,
            input_power: P_in,
            output_power: P_out,
            efficiency: 100, // 100% for ideal
            reflected_impedance: Z_reflected,
            is_step_up: turns_ratio > 1 ? 1 : 0,
            is_step_down: turns_ratio < 1 ? 1 : 0,
        };
    },
    energyComputation: (state, params) => {
        const V_in = getState(state, 'V_in');
        const N_p = getParam(params, 'N_primary');
        const N_s = getParam(params, 'N_secondary');
        const R_load = getParam(params, 'R_load');
        const V_out = V_in * (N_s / N_p);
        const P_out = (V_out * V_out) / R_load;
        return { kinetic: 0, potential: 0, thermal: 0, total: P_out };
    },
    failureModes: [
        {
            id: 'saturation-warning',
            name: 'Saturation Risk',
            check: (state, params) => {
                const V_in = getState(getStateValues(state), 'V_in');
                const f = getParam(params, 'f');
                const V_per_hz = V_in / f;
                return V_per_hz > 3;
            },
            severity: 'warning',
            educationalMessage: 'The voltage-to-frequency ratio is high. In a real transformer, this could cause core saturation, leading to distortion and overheating.',
        },
    ],
    bounds: {
        V_in: { min: 0, max: 500 },
    },
});
// ============================================================================
// LC OSCILLATIONS
// ============================================================================
export const LCOscillations = createExperimentFromSchema({
    id: 'lc-oscillations',
    name: 'LC Oscillations',
    domain: 'physics',
    description: 'Natural oscillations in an ideal LC circuit (electromagnetic oscillator)',
    conceptTags: ['LC Circuit', 'Electromagnetic Oscillations', 'Resonance', 'Energy Exchange'],
    difficulty: 5,
    stateVariables: [
        { id: 'Q', name: 'Charge', unit: 'C', initial: 1e-6, symbol: 'Q' },
        { id: 'I', name: 'Current', unit: 'A', initial: 0, symbol: 'I' },
    ],
    parameters: [
        { id: 'L', name: 'Inductance', unit: 'H', min: 0.001, max: 10, default: 0.1, symbol: 'L' },
        { id: 'C', name: 'Capacitance', unit: 'F', min: 1e-9, max: 0.1, default: 1e-5, symbol: 'C' },
        { id: 'R', name: 'Resistance (Damping)', unit: 'Ω', min: 0, max: 100, default: 0, symbol: 'R', description: 'Set to 0 for ideal undamped oscillations' },
    ],
    equations: [
        {
            id: 'lc-frequency',
            name: 'Natural Frequency',
            latex: '\\omega_0 = \\frac{1}{\\sqrt{LC}}',
            description: 'Angular frequency of LC oscillations',
        },
        {
            id: 'lc-equation',
            name: 'LC Circuit Equation',
            latex: 'L\\frac{d^2Q}{dt^2} + R\\frac{dQ}{dt} + \\frac{Q}{C} = 0',
            description: 'Differential equation for charge (with damping)',
        },
        {
            id: 'period',
            name: 'Period',
            latex: 'T = 2\\pi\\sqrt{LC}',
            description: 'Time for one complete oscillation',
        },
        {
            id: 'energy-conservation',
            name: 'Energy Conservation (Undamped)',
            latex: '\\frac{Q^2}{2C} + \\frac{LI^2}{2} = \\text{constant}',
            description: 'Energy oscillates between electric and magnetic',
        },
    ],
    derivatives: (state, params) => {
        const Q = getState(state, 'Q');
        const I = getState(state, 'I');
        const L = getParam(params, 'L');
        const C = getParam(params, 'C');
        const R = getParam(params, 'R');
        // Kirchhoff's law: L*dI/dt + R*I + Q/C = 0
        const dI_dt = -(R * I + Q / C) / L;
        return {
            Q: I,
            I: dI_dt,
        };
    },
    derivedState: (_state, params) => {
        const L = getParam(params, 'L');
        const C = getParam(params, 'C');
        const R = getParam(params, 'R');
        const omega_0 = 1 / Math.sqrt(L * C);
        const f_0 = omega_0 / (2 * Math.PI);
        const period = 2 * Math.PI * Math.sqrt(L * C);
        // Damping analysis
        const alpha = R / (2 * L); // Damping coefficient
        const is_underdamped = alpha < omega_0;
        const damped_freq = is_underdamped ? Math.sqrt(omega_0 * omega_0 - alpha * alpha) : 0;
        const Q_factor = omega_0 * L / R;
        return {
            natural_frequency: omega_0,
            frequency_hz: f_0,
            period,
            damping_coefficient: alpha,
            is_underdamped: is_underdamped ? 1 : 0,
            damped_frequency: damped_freq,
            quality_factor: R > 0 ? Q_factor : 1e6,
            time_constant: R > 0 ? 2 * L / R : 1e6,
        };
    },
    energyComputation: (state, params) => {
        const Q = getState(state, 'Q');
        const I = getState(state, 'I');
        const L = getParam(params, 'L');
        const C = getParam(params, 'C');
        const E_electric = 0.5 * Q * Q / C;
        const E_magnetic = 0.5 * L * I * I;
        return {
            kinetic: E_magnetic, // Magnetic energy
            potential: E_electric, // Electric energy
            thermal: 0,
            total: E_electric + E_magnetic,
        };
    },
    failureModes: [
        {
            id: 'no-oscillation',
            name: 'No Oscillation (Overdamped)',
            check: (_state, params) => {
                const L = getParam(params, 'L');
                const C = getParam(params, 'C');
                const R = getParam(params, 'R');
                const alpha = R / (2 * L);
                const omega_0 = 1 / Math.sqrt(L * C);
                return R > 0 && alpha >= omega_0;
            },
            severity: 'warning',
            educationalMessage: 'The circuit is overdamped or critically damped. Oscillations will not occur; the charge will simply decay without oscillating.',
        },
    ],
});
//# sourceMappingURL=electromagnetism.js.map